import {
  DynamicFormAttachmentsContextData,
  EditorConfig,
  FieldType,
  FormActionData,
  FormBuiltInActions,
  InsertMode,
  LayoutBreakpoints,
  RenderForm,
  ToolbarAction,
} from "@entaconsulting/form";
import { Box, Theme, createTheme } from "@mui/material";
import { ThemeProvider } from "@mui/material/styles";
import { useResourcePath } from "common/hooks/useResourcePath";
import React, { useCallback, useEffect, useMemo } from "react";
import {
  addAttachments,
  cancelOperation,
  purgeOperations,
  removeAttachment,
  replaceAttachments,
  retryOperation,
  submitAll,
} from "store/attachments/attachmentsSlice";
import { saveSampleData } from "store/form/formDataSlice";
import { useAppDispatch, useAppSelector } from "store/hooks";
import useDebouncedFormDefinition from "../hooks/useDebouncedFormDefinition";
import { FIELD_TYPE_OPTIONS } from "../hooks/useInsertionMenuOptions";
import useIsInsertionModeEnabled from "../hooks/useIsInsertionModeEnabled";
import {
  clearVisualEditorSelectionState,
  insertElement,
  onMoveElement,
  setSelectedRoute,
} from "../store/editorSlice";
import getLayoutElementAtPath from "../utils/getLayoutElementAtPath";
import PreviewErrorHandler from "./previewErrorHandler";

const getTheme = (theme: Theme) => {
  return createTheme({
    ...theme,
    components: {
      MuiTextField: {
        defaultProps: {
          variant: "standard",
        },
      },
    },
  });
};

const DebouncedRenderForm: React.FC<{}> = React.memo(() => {
  const dispatch = useAppDispatch();
  const formDefinitionPreview = useDebouncedFormDefinition();

  const selectedRoute = useAppSelector(
    (state) => state.developer.edit.editor.selectedRoute
  );
  const editorMode = useAppSelector(
    (state) => state.developer.edit.toolbar.editorMode
  );
  const { initialValues, isSubmitting } = useAppSelector((state) => ({
    initialValues: state.formData.initialValues,
    isSubmitting: state.formData.isSubmitting,
  }));

  const attachmentsState = useAppSelector((state) => state.attachments);
  const attachments: DynamicFormAttachmentsContextData = useMemo(
    () => ({
      ...attachmentsState,
      addAttachments: (fieldName, filesToAdd) =>
        dispatch(addAttachments(fieldName, filesToAdd)),
      replaceAttachments: (fieldName, filesToAdd) =>
        dispatch(replaceAttachments(fieldName, filesToAdd)),
      removeAttachment: (fieldName, fileName) =>
        dispatch(removeAttachment(fieldName, fileName)),
      retryOperation: (fieldName, fileName) =>
        dispatch(retryOperation(fieldName, fileName)),
      cancelOperation: (fieldName, fileName) =>
        dispatch(cancelOperation(fieldName, fileName)),
      purgeOperations: (fieldName, fileNames) =>
        dispatch(purgeOperations(fieldName, fileNames)),
      submitAll: (fieldName, operationFilter, uploadFiles) =>
        dispatch(submitAll(fieldName, operationFilter, uploadFiles)),
    }),
    [attachmentsState, dispatch]
  );
  const getResourcePath = useResourcePath();

  useEffect(() => {
    return () => {
      dispatch(clearVisualEditorSelectionState());
    };
  }, [dispatch]);

  const lastSavedDate = useMemo(() => new Date(), []);

  const handleLoadSampleData = useCallback(
    (data: FormActionData, formBuiltInActions?: FormBuiltInActions) => {
      formBuiltInActions?.restart(initialValues);
    },
    [initialValues]
  );

  const handleValidate = useCallback(() => {}, []);

  const handleSaveSampleData = useCallback(
    (data: FormActionData) => {
      dispatch(saveSampleData(data.values));
    },
    [dispatch]
  );

  const handleClearSampleData = useCallback(
    (data: FormActionData, formBuiltInActions?: FormBuiltInActions) => {
      formBuiltInActions?.restart({});
    },
    []
  );

  const handleSelectEditorCallback = useCallback(
    (routeToElement: string) => {
      dispatch(setSelectedRoute(routeToElement));
    },
    [dispatch]
  );

  const handleMoveCallback = useCallback(
    (
      srcPath: string,
      destPath: string,
      insertAt: number,
      mode: InsertMode,
      activeBreakpoint: NonNullable<LayoutBreakpoints>
    ) => {
      dispatch(
        onMoveElement({ srcPath, destPath, insertAt, mode, activeBreakpoint })
      );
    },
    [dispatch]
  );
  const handleInsertEditorCallback = useCallback(
    (
      routeToElement: string,
      insertAt: number,
      mode: InsertMode,
      activeBreakpoint: NonNullable<LayoutBreakpoints>
    ) => {
      const isFieldInsertionMode = editorMode.mode.endsWith("field");
      const isImageInsertionMode = editorMode.mode.endsWith("image");
      const isTextInsertionMode = editorMode.mode.endsWith("text");
      const isSectionInsertionMode =
        editorMode.mode.endsWith("layout") && routeToElement === "body";
      const isColumnInsertionMode =
        editorMode.mode.endsWith("layout") && routeToElement !== "body";

      const elementType = isFieldInsertionMode
        ? "field"
        : isImageInsertionMode
        ? "image"
        : isTextInsertionMode
        ? "text"
        : isColumnInsertionMode
        ? "column"
        : isSectionInsertionMode
        ? "section"
        : "table";

      const fieldType: FieldType | undefined =
        elementType === "table"
          ? "list"
          : FIELD_TYPE_OPTIONS.find(
              (option) => option.id === editorMode.fieldTypeIdMode
            )?.value ?? undefined;

      dispatch(
        insertElement({
          routeToElement,
          insertAt,
          mode,
          elementType,
          fieldType,
          activeBreakpoint,
        })
      );
    },
    [dispatch, editorMode.fieldTypeIdMode, editorMode.mode]
  );

  const handleCanInsertEditorCallback = useCallback(
    (containerPath: string): boolean => {
      let layoutElement = getLayoutElementAtPath(
        containerPath,
        formDefinitionPreview.layout
      );

      const isAbleToInsertField: boolean =
        layoutElement?.type === "column" &&
        ["insert-field", "insert-text", "insert-image"].includes(
          editorMode.mode
        );

      const isAbleToInsertLayout: boolean =
        editorMode.mode === "insert-layout" &&
        (["body", "header"].includes(containerPath) ||
          layoutElement?.type === "section");

      const isAbleToInsertTable: boolean =
        editorMode.mode === "insert-table" &&
        (["body", "header"].includes(containerPath) ||
          layoutElement?.type === "section" ||
          layoutElement?.type === "column");

      if (isAbleToInsertField || isAbleToInsertLayout || isAbleToInsertTable)
        return true;

      return false;
    },
    [formDefinitionPreview.layout, editorMode.mode]
  );

  const toolbarActions: ToolbarAction[] = useMemo(() => {
    return [
      {
        key: "loadSampleData",
        label: "Cargar datos de ejemplo",
        validationRequired: false,
        enabled: !isSubmitting,
        onAction: handleLoadSampleData,
      },
      {
        key: "validate",
        label: "Validar",
        validationRequired: true,
        enabled: !isSubmitting,
        onAction: handleValidate,
      },
      {
        key: "saveSampleData",
        label: "Guardar datos de ejemplo",
        validationRequired: false,
        enabled: !isSubmitting,
        onAction: handleSaveSampleData,
      },
      {
        key: "clearSampleData",
        label: "Borrar datos de ejemplo",
        validationRequired: false,
        enabled: !isSubmitting,
        onAction: handleClearSampleData,
      },
    ];
  }, [
    isSubmitting,
    handleLoadSampleData,
    handleValidate,
    handleSaveSampleData,
    handleClearSampleData,
  ]);

  const editorConfig: EditorConfig = useMemo(
    () => ({
      onInsert: handleInsertEditorCallback,
      onSelect: handleSelectEditorCallback,
      design: !editorMode.isLive,
      editorMode: editorMode.mode === "select" ? "select" : "insert",
      canInsert: handleCanInsertEditorCallback,
      onMove: handleMoveCallback,
    }),
    [
      handleInsertEditorCallback,
      handleSelectEditorCallback,
      editorMode.isLive,
      editorMode.mode,
      handleCanInsertEditorCallback,
      handleMoveCallback,
    ]
  );
  const editorInitialValues = useMemo(
    () => ({ selectedPath: selectedRoute }),
    [selectedRoute]
  );

  const isInsertionModeEnabled = useIsInsertionModeEnabled();

  return (
    <ThemeProvider theme={getTheme}>
      <Box
        component="span"
        sx={{
          cursor: isInsertionModeEnabled ? "copy" : "default",
        }}
      >
        <PreviewErrorHandler resetObject={formDefinitionPreview}>
          <RenderForm
            formDefinition={formDefinitionPreview}
            initialValues={initialValues}
            isSubmitting={isSubmitting}
            attachments={attachments}
            status="Preview"
            lastSaved={lastSavedDate}
            allowEdit={!isSubmitting}
            getResourcePath={getResourcePath}
            toolbarActions={toolbarActions}
            editorConfig={editorConfig}
            editorInitialValues={editorInitialValues}
          />
        </PreviewErrorHandler>
      </Box>
    </ThemeProvider>
  );
});

DebouncedRenderForm.displayName = "DebouncedRenderForm";

export default DebouncedRenderForm;
